<script lang="ts">
  import { cn } from "$lib/utils";
  import { mode, resetMode } from "mode-watcher";
  import BlogIcons from "$lib/components/blog/blog-icons.svelte";
  import Questions from "$lib/components/blog/questions.svelte";
  import logoNormal from "$lib/images/logo/logo_light.svg";
  import logoDark from "$lib/images/logo/logo_dark.svg";
  import { page } from "$app/state";
  import { onMount } from "svelte";

  let { fullWidth = true } = $props();

  type Section = {
    title: string;
    items: { label: string; href: string; newTab?: boolean; disabled?: boolean }[];
  };

  const isBlogPage = $derived(page.url.pathname.startsWith("/blog"));

  const footerSections: Section[] = [
    {
      title: "Features",
      items: [
        { label: "AI Web Scraping", href: "/features/ai-web-scraping" },
        { label: "AI PDF Processing", href: "/features/ai-pdf-processing" },
      ],
    },
    {
      title: "Use Cases",
      items: [
        { label: "AI E-Commerce Automation", href: "/use-cases/ai-ecommerce-automation" },
        { label: "AI Resume Screening", href: "/use-cases/recruiting-ai-resume-screening" },
      ],
    },
    {
      title: "Resources",
      items: [{ label: "Affiliate Program", href: "/affiliates" }],
    },
    {
      title: "Company",
      items: [
        { label: "Blog", href: "/blog" },
        { label: "Pricing", href: "/plans" },
        { label: "About", href: "/about" },
      ],
    },
    {
      title: "Discover",
      items: [
        { label: "Companies", href: "/explore/companies" },
        { label: "People", href: "/explore/people" },
      ],
    },
    {
      title: "Legal",
      items: [
        { label: "Master Subscription Agreement", href: "/msa" },
        { label: "Terms of Use", href: "/policies/terms" },
        { label: "Privacy Policy", href: "/policies/privacy" },
      ],
    },
  ];

  let logo = $derived($mode !== "dark" ? logoNormal : logoDark);

  onMount(() => {
    resetMode();
  });
</script>

<footer
  class={cn(
    "w-full bg-theme-background-primary px-6 py-16 dark:bg-[#09090B] sm:px-8 md:px-10 lg:py-24",
    isBlogPage ? "dark:!bg-[var(--dark-background-secondary)]" : "",
    !fullWidth && "mx-auto max-w-screen-2xl lg:px-12",
  )}
>
  <div class="mx-auto max-w-screen-2xl w-full">
    <div class="grid grid-cols-1 gap-12 lg:hidden">
      <div>
        <img src={logo} alt="Chatsheet logo" class="mb-8 w-[11rem]" />
      </div>

      <div class="text-blog-gray-1000 dark:text-blog-gray-300">
        <div class="grid grid-cols-2 gap-x-10 gap-y-12 sm:grid-cols-3">
          {#each footerSections as section}
            <div>
              <div class="mb-6 text-lg font-bold leading-6">
                {section.title}
              </div>
              <ul class="space-y-4">
                {#each section.items as item}
                  <li>
                    {#if item.disabled}
                      <span class="text-base font-normal leading-5 opacity-30">
                        {item.label}
                      </span>
                    {:else}
                      <a
                        href={item.href}
                        class="text-base font-normal leading-5 hover:underline"
                        target={item.newTab ? "_blank" : "_self"}
                      >
                        {item.label}
                      </a>
                    {/if}
                  </li>
                {/each}
              </ul>
            </div>
          {/each}
        </div>
      </div>

      <div>
        <Questions css="block" />
      </div>

      <div class="border-t border-gray-200 dark:border-gray-800 pt-8 text-base font-light text-theme-text-secondary">
        <div class="flex items-center gap-6 mb-6">
          <a href="https://linkedin.com/company/chatsheet" class="hover:opacity-80" aria-label="LinkedIn">
            <BlogIcons name="linkedin" size={24} />
          </a>
          <a href="https://www.youtube.com/@chatsheet" class="hover:opacity-80" aria-label="YouTube">
            <BlogIcons name="youtube" size={24} />
          </a>
          <a href="https://x.com/chatsheetai" class="hover:opacity-80" aria-label="Twitter">
            <BlogIcons name="twitter" size={24} />
          </a>
        </div>
        <p class="text-base font-normal">
          © {new Date().getFullYear()} Chatsheet AI. All rights reserved.
        </p>
      </div>
    </div>

    <div class="hidden lg:block">
      <div class="flex flex-col gap-16">
        <div class="flex justify-between gap-16 xl:gap-24">
          <div class="max-w-xs">
            <img src={logo} alt="Chatsheet logo" class="mb-12 w-[12rem]" />
            <Questions css="block" />
          </div>

          <div class="flex-1 text-blog-gray-1000 dark:text-blog-gray-300">
            <div class="grid grid-cols-3 gap-x-10 gap-y-16 xl:grid-cols-6">
              {#each footerSections as section}
                <div>
                  <div class="mb-6 text-lg font-bold leading-6">
                    {section.title}
                  </div>
                  <ul class="space-y-4">
                    {#each section.items as item}
                      <li>
                        {#if item.disabled}
                          <span class="text-base font-normal leading-5 opacity-30">
                            {item.label}
                          </span>
                        {:else}
                          <a
                            href={item.href}
                            class="text-base font-normal leading-5 hover:underline"
                            target={item.newTab ? "_blank" : "_self"}
                          >
                            {item.label}
                          </a>
                        {/if}
                      </li>
                    {/each}
                  </ul>
                </div>
              {/each}
            </div>
          </div>
        </div>

        <div class="border-0 border-theme-border-subtle pt-8">
          <div class="flex justify-between items-center">
            <div></div>
            <div class="flex flex-col items-end text-theme-text-secondary">
              <div class="flex items-center gap-6 mb-4">
                <a href="https://linkedin.com/company/chatsheet" class="hover:opacity-80" aria-label="LinkedIn">
                  <BlogIcons name="linkedin" size={24} />
                </a>
                <a href="https://www.youtube.com/@chatsheet" class="hover:opacity-80" aria-label="YouTube">
                  <BlogIcons name="youtube" size={24} />
                </a>
                <a href="https://x.com/chatsheetai" class="hover:opacity-80" aria-label="Twitter">
                  <BlogIcons name="twitter" size={24} />
                </a>
              </div>
              <p class="text-base font-normal">
                © {new Date().getFullYear()} Chatsheet AI. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</footer>
